// ============================================================
// COMMISSION SETTINGS PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { Percent, Save, RefreshCw } from 'lucide-react';
import { Table, Badge, Button, Modal, StatCard } from '../components/ui';
import { getCommissionData } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatCurrency } from '../utils/helpers';
import { toast } from 'react-toastify';

const CommissionSettings = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [rate, setRate] = useState('');

  useEffect(() => {
    adminApi.getCommissionSettings(getCommissionData)
      .then(res => {
        const arr = Array.isArray(res) ? res : (res?.items || []);
        setData(arr.map(r => ({ ...r, restaurantName: r.restaurantName || r.restaurant || r.name, customRate: r.customRate ?? r.commissionRate ?? 15 })));
        setLoading(false);
      })
      .catch(e => {
        setError(e.message || 'Failed to load data');
        setLoading(false);
      });
  }, []);

  const openEdit = (row) => { setEditRow(row); setRate(String(row.customRate)); };

  const saveRate = async () => {
    const v = parseFloat(rate);
    if (isNaN(v) || v < 0 || v > 50) { toast.error('Enter a valid rate between 0-50%'); return; }
    try { await adminApi.updateCommissionRate(editRow.id, v); } catch { /* fallback */ }
    setData(p => p.map(r => r.id === editRow.id ? { ...r, customRate: v } : r));
    toast.success('Commission rate updated!');
    setEditRow(null);
  };

  const resetAll = () => {
    setData(p => p.map(r => ({ ...r, customRate: r.commissionRate })));
    toast.info('All rates reset to defaults');
  };

  const avgRate = data.length ? (data.reduce((s, r) => s + r.customRate, 0) / data.length).toFixed(1) : 0;
  const totalExpected = data.reduce((s, r) => s + r.totalOrders * r.avgOrderValue * r.customRate / 100, 0);

  const columns = [
    { key: 'restaurantName', label: 'Restaurant', render: v => <b>{v}</b> },
    { key: 'commissionRate', label: 'Default Rate', render: v => <Badge>{v}%</Badge> },
    {
      key: 'customRate', label: 'Current Rate',
      render: (v, row) => (
        <span style={{ fontWeight: 700, color: v !== row.commissionRate ? '#E85D04' : 'inherit' }}>
          {v}% {v !== row.commissionRate && <span style={{ fontSize: 11, color: '#94A3B8' }}>(custom)</span>}
        </span>
      )
    },
    { key: 'totalOrders', label: 'Orders' },
    { key: 'avgOrderValue', label: 'Avg Order', render: v => formatCurrency(v) },
    { key: 'status', label: 'Status', render: v => <Badge status={v} /> },
    {
      key: 'id', label: 'Action',
      render: (_, row) => (
        <Button size="sm" variant="primary" icon={<Percent size={12} />} onClick={() => openEdit(row)}>Edit Rate</Button>
      )
    },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Commission Settings</h1>
          <p className="admin-page-subtitle">Configure commission rates per restaurant</p>
        </div>
        <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={resetAll}>Reset All Defaults</Button>
      </div>

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 24 }}>
        <StatCard label="Average Rate" value={`${avgRate}%`} icon={<Percent size={18} />} color="#3B82F6" />
        <StatCard label="Custom Rates" value={data.filter(r => r.customRate !== r.commissionRate).length} icon={<Percent size={18} />} color="#F59E0B" />
        <StatCard label="Expected Monthly" value={formatCurrency(totalExpected)} icon={<Percent size={18} />} color="#10B981" />
      </div>

      {/* Global Setting Banner */}
      <div className="admin-card" style={{ marginBottom: 24, background: 'linear-gradient(135deg, #E85D04 0%, #F97316 100%)' }}>
        <div className="admin-card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 20, color: '#fff' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Global Default Commission Rate</h3>
            <p style={{ margin: '4px 0 0', opacity: 0.85, fontSize: 13 }}>Applied to all new restaurants unless overridden</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 32, fontWeight: 800 }}>15%</span>
            <Button variant="secondary" size="sm">Change</Button>
          </div>
        </div>
      </div>

      <div className="admin-table-wrapper">
        <div className="admin-table-toolbar">
          <span style={{ fontSize: 14, fontWeight: 700 }}>{data.length} Restaurants</span>
        </div>
        <Table columns={columns} data={data} loading={loading} />
      </div>

      <Modal open={!!editRow} onClose={() => setEditRow(null)} title={`Edit Commission — ${editRow?.restaurantName}`}
        footer={<>
          <Button variant="secondary" onClick={() => setEditRow(null)}>Cancel</Button>
          <Button variant="primary" icon={<Save size={14} />} onClick={saveRate}>Save Rate</Button>
        </>}>
        {editRow && (
          <div>
            <div className="admin-info-grid">
              <div className="admin-info-item"><span className="admin-info-label">Restaurant</span><span className="admin-info-value">{editRow.restaurantName}</span></div>
              <div className="admin-info-item"><span className="admin-info-label">Default Rate</span><span className="admin-info-value">{editRow.commissionRate}%</span></div>
            </div>
            <div className="admin-form-group" style={{ marginTop: 16 }}>
              <label>Commission Rate (%)</label>
              <input type="number" className="admin-form-control" value={rate} onChange={e => setRate(e.target.value)} min="0" max="50" step="0.5" />
              <span style={{ fontSize: 12, color: '#94A3B8' }}>Valid range: 0% – 50%</span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CommissionSettings;
